<script setup lang="ts">
import { computed, type PropType, ref, watch } from 'vue';
import { Icon } from '@iconify/vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import {formatedDate} from '@/constants'
// @ts-ignore
import html2pdf from 'html2pdf.js';
import type { CostToSaleAnalysisReportResponse } from '@/types/report-types';
import { CurrencyType } from '@/types/currency-types';
import { getProductById } from '@/api/products';

const props = defineProps({
  visible: { type: Boolean, required: true },
  onClose: { type: Function as PropType<() => void>, required: true },
  reportData: { type: Object as PropType<CostToSaleAnalysisReportResponse | null>, default: null },
  startDate: { type: Date as PropType<Date | null>, default: null },
  endDate: { type: Date as PropType<Date | null>, default: null },
  qualityId: { type: String as PropType<string | null>, default: null },
  currency: { type: Number, default: 0 },
  companyName: { type: String, default: 'Techknital Fabrics Ltd' },
  companyAddress: { type: String, default: 'Unit 8 - 9, Lime Kilns Business Park\nLime Kilns Way\nHinckley\nLeicestershire, LE10 #EL' },
  companyTel: { type: String, default: '+44 (0)1455 254350' },
  companyFax: { type: String, default: '+44 (0)1455 238998' },
});

const PDF_EXPORT_ELEMENT_ID = 'cost-to-sale-analysis-report-content';
const qualityNameDisplay = ref<string | null>(null);
const isLoadingQualityName = ref(false);

const formattedStartDate = computed(() =>
    props.startDate ? formatedDate(props.startDate) : 'N/A'
);

const formattedEndDate = computed(() =>
    props.endDate ? formatedDate(props.endDate) : 'N/A'
);

const currencyInfo = computed(() => {
  return CurrencyType.value.find(c => c.id === props.currency) || CurrencyType.value[0];
});

const qualityDisplayName = computed(() => {
  if (isLoadingQualityName.value) return "Loading quality...";
  return qualityNameDisplay.value || (props.qualityId ? `Quality ID: ${props.qualityId}` : 'N/A');
});

watch([() => props.visible, () => props.qualityId], async ([newVisibleState, newQualityId]) => {
  if (newVisibleState && newQualityId && typeof newQualityId === 'string') {
    isLoadingQualityName.value = true;
    try {
      const product = await getProductById(newQualityId);
      qualityNameDisplay.value = product.finishedReference || product.unfinishedReference || "Unknown Quality";
    } catch (error) {
      console.error("Failed to fetch quality name:", error);
      qualityNameDisplay.value = `Quality ID: ${newQualityId}`;
    } finally {
      isLoadingQualityName.value = false;
    }
  } else if (!newVisibleState) {
    qualityNameDisplay.value = null;
  }
}, { immediate: true });

const onDownloadReportPdf = () => {
  const reportElement = document.getElementById(PDF_EXPORT_ELEMENT_ID);
  if (!reportElement || !props.reportData) return;
  const filename = `cost-to-sale-analysis-${qualityNameDisplay.value || props.qualityId || 'na'}-${new Date().toISOString().split('T')[0]}.pdf`;
  html2pdf().set({ margin: [10, 5, 10, 5], filename, image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' } }).from(reportElement).save();
};

const hasReportData = computed(() => !!(props.reportData?.qualities?.length));

const formatDateForTable = (dateString?: Date | null) => dateString ? new Date(dateString).toLocaleDateString('en-GB') : '-';
const formatCurrency = (value?: number) => typeof value === 'number' ? `${currencyInfo.value.symbol} ${value.toFixed(2)}` : `${currencyInfo.value.symbol} 0.00`;
const formatNumberTwoDecimals = (value?: number) => typeof value === 'number' ? value.toFixed(2) : '-';
const formatPercentage = (value?: number) => typeof value === 'number' ? `${value.toFixed(2)}%` : '-';

const tableData = computed(() => {
  if (!props.reportData?.qualities) return [];

  const flatData: Array<{
    qualityReference: string;
    batchNumber: string;
    dateKnitted: Date | null;
    totalWeightKg: number;
    totalLengthMetres: number;
    knittingCost: number;
    finishingCost: number;
    yarnCost: number;
    fireTestCost: number;
    qualityCheckCost: number;
    totalBatchProductionCost: number;
    totalBatchSalesValue: number;
    batchProfitMarginPercentage: number;
    costPerKg: number;
    costPerMetre: number;
  }> = [];

  props.reportData.qualities.forEach(quality => {
    quality.batches.forEach(batch => {
      flatData.push({
        qualityReference: quality.qualityReference,
        batchNumber: batch.batchNumber,
        dateKnitted: batch.dateKnitted,
        totalWeightKg: batch.totalWeightKg,
        totalLengthMetres: batch.totalLengthMetres,
        knittingCost: batch.productionCosts.knittingCost,
        finishingCost: batch.productionCosts.finishingCost,
        yarnCost: batch.productionCosts.yarnCost,
        fireTestCost: batch.productionCosts.fireTestCost,
        qualityCheckCost: batch.productionCosts.qualityCheckCost,
        totalBatchProductionCost: batch.totalBatchProductionCost,
        totalBatchSalesValue: batch.totalBatchSalesValue,
        batchProfitMarginPercentage: batch.batchProfitMarginPercentage,
        costPerKg: batch.costPerKg,
        costPerMetre: batch.costPerMetre
      });
    });
  });

  return flatData;
});

</script>

<template>
  <Dialog :visible="visible" modal :draggable="false" :style="{ width: '95rem', maxWidth: '98vw', minHeight: '90vh'}" :closable="false" @update:visible="onClose" class="report-dialog-custom-header">
    <template #header>
      <div class="flex justify-between items-center w-full">
        <Button link @click="onClose" aria-label="Back to report page" class="flex items-center gap-x-1 p-0 text-gray-600 hover:text-primary-default">
          <Icon icon="material-symbols:arrow-back-rounded" class="text-2xl md:text-3xl group-hover:-translate-x-1 transition-transform" />
          <span class="text-sm md:text-base hidden sm:inline">Back to Report Page</span>
        </Button>
        <div class="flex-grow text-center">
          <h2 class="text-lg md:text-xl font-semibold">Cost to Sale Analysis Report</h2>
        </div>
        <div class="flex items-center gap-x-2">
          <Button label="Download as PDF" @click="onDownloadReportPdf" :disabled="!hasReportData || isLoadingQualityName" class="p-button-danger text-sm md:text-base">
            <template #icon><Icon icon="material-symbols:download-2" class="text-xl ml-3 order-1" /></template>
          </Button>
        </div>
      </div>
    </template>

    <div class="dialog-main-content flex-grow overflow-y-auto">
      <div v-if="(!reportData && visible) || (isLoadingQualityName && hasReportData)" class="text-center py-10 text-gray-500">
        {{ isLoadingQualityName ? 'Loading quality details...' : (reportData ? 'Loading report data...' : 'No data to display.') }}
      </div>
      <div v-else-if="hasReportData" :id="PDF_EXPORT_ELEMENT_ID" class="p-2 md:p-4 bg-white">
        <div class="flex justify-between items-start mb-6">
          <div><img src="@/assets/images/logo.png" alt="Company Logo" class="h-12 mb-1" /></div>
          <div class="text-right text-xs text-gray-500 whitespace-pre-line"><p>{{ companyAddress }}</p><p>Tel: {{ companyTel }}</p><p>Fax: {{ companyFax }}</p></div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-md shadow-sm">
          <div><h3 class="text-md font-semibold text-gray-700">Report Period</h3><p class="text-sm text-gray-600">Start Date: {{ formattedStartDate }}</p><p class="text-sm text-gray-600">End Date: {{ formattedEndDate }}</p></div>
          <div class="md:text-right">
            <h3 class="text-md font-semibold text-gray-700">Cost to Sale Analysis</h3>
            <p class="text-sm text-gray-600">Quality: {{ qualityDisplayName }}</p>
            <p class="text-sm text-gray-600">Currency: {{ currencyInfo.name }} ({{ currencyInfo.symbol }})</p>
          </div>
        </div>

        <!-- Summary Cards -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 class="text-sm font-semibold text-blue-800 mb-2">Total Production Cost</h4>
            <p class="text-xl font-bold text-blue-900">{{ formatCurrency(reportData?.totalProductionCost) }}</p>
          </div>
          <div class="bg-green-50 p-4 rounded-lg border border-green-200">
            <h4 class="text-sm font-semibold text-green-800 mb-2">Total Sales Value</h4>
            <p class="text-xl font-bold text-green-900">{{ formatCurrency(reportData?.totalSalesValue) }}</p>
          </div>
          <div class="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h4 class="text-sm font-semibold text-purple-800 mb-2">Overall Profit Margin</h4>
            <p class="text-xl font-bold text-purple-900">{{ formatPercentage(reportData?.overallProfitMarginPercentage) }}</p>
          </div>
        </div>

        <div class="overflow-x-auto">
          <DataTable
              :value="tableData"
              responsiveLayout="scroll"
              class="p-datatable-sm cost-to-sale-analysis-table min-w-full"
              stripedRows
              :rowHover="true"
              :scrollable="true"
              scrollHeight="60vh"
          >
            <Column field="qualityReference" header="Quality Ref." sortable :style="{ minWidth: '120px', whiteSpace: 'nowrap' }"></Column>
            <Column field="batchNumber" header="Batch No." sortable :style="{ minWidth: '100px', whiteSpace: 'nowrap' }"></Column>
            <Column field="dateKnitted" header="Date Knitted" sortable :style="{ minWidth: '120px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatDateForTable(slotProps.data.dateKnitted) }}</template>
            </Column>
            <Column field="totalWeightKg" header="Weight (kg)" sortable :style="{ minWidth: '100px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatNumberTwoDecimals(slotProps.data.totalWeightKg) }}</template>
            </Column>
            <Column field="totalLengthMetres" header="Length (m)" sortable :style="{ minWidth: '100px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatNumberTwoDecimals(slotProps.data.totalLengthMetres) }}</template>
            </Column>
            <Column field="yarnCost" header="Yarn Cost" sortable :style="{ minWidth: '100px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatCurrency(slotProps.data.yarnCost) }}</template>
            </Column>
            <Column field="knittingCost" header="Knitting Cost" sortable :style="{ minWidth: '110px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatCurrency(slotProps.data.knittingCost) }}</template>
            </Column>
            <Column field="finishingCost" header="Finishing Cost" sortable :style="{ minWidth: '120px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatCurrency(slotProps.data.finishingCost) }}</template>
            </Column>
            <Column field="fireTestCost" header="Fire Test Cost" sortable :style="{ minWidth: '120px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatCurrency(slotProps.data.fireTestCost) }}</template>
            </Column>
            <Column field="qualityCheckCost" header="Quality Check Cost" sortable :style="{ minWidth: '140px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatCurrency(slotProps.data.qualityCheckCost) }}</template>
            </Column>
            <Column field="totalBatchProductionCost" header="Total Production Cost" sortable :style="{ minWidth: '160px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatCurrency(slotProps.data.totalBatchProductionCost) }}</template>
            </Column>
            <Column field="totalBatchSalesValue" header="Total Sales Value" sortable :style="{ minWidth: '140px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatCurrency(slotProps.data.totalBatchSalesValue) }}</template>
            </Column>
            <Column field="batchProfitMarginPercentage" header="Profit Margin %" sortable :style="{ minWidth: '130px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatPercentage(slotProps.data.batchProfitMarginPercentage) }}</template>
            </Column>
            <Column field="costPerKg" header="Cost per Kg" sortable :style="{ minWidth: '100px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatCurrency(slotProps.data.costPerKg) }}</template>
            </Column>
            <Column field="costPerMetre" header="Cost per Metre" sortable :style="{ minWidth: '120px', whiteSpace: 'nowrap' }">
              <template #body="slotProps">{{ formatCurrency(slotProps.data.costPerMetre) }}</template>
            </Column>

            <ColumnGroup type="footer" v-if="reportData && reportData.qualities.length > 0">
              <Row>
                <Column footer="Totals:" :colspan="10" footerStyle="text-align:left; font-weight: bold; color: #1f2937; padding-left: 0.5rem;" />
                <Column :footer="formatCurrency(reportData.totalProductionCost)" footerStyle="text-align:right; font-weight: bold; color: #1f2937;" />
                <Column :footer="formatCurrency(reportData.totalSalesValue)" footerStyle="text-align:right; font-weight: bold; color: #1f2937;" />
                <Column :footer="formatPercentage(reportData.overallProfitMarginPercentage)" footerStyle="text-align:right; font-weight: bold; color: #1f2937;" />
                <Column footer="" :colspan="2" footerStyle="text-align:right; font-weight: bold; color: #1f2937;" />
              </Row>
            </ColumnGroup>
          </DataTable>
        </div>

      </div>
      <div v-else-if="!hasReportData && visible" class="text-center py-10 text-gray-500">
        No cost analysis data found for the selected quality and date range.
      </div>
    </div>
  </Dialog>
</template>

<style scoped></style>
